<template>
  <HealthLogout v-model="modals.logout" />

  <section
    v-bind="$attrs"
    :class="[display, align, gap]"
    class="cursor-pointer group bg-transparent hover:bg-primary-200/10 transition-colors duration-300 rounded-lg pl-1 lg:pl-3 pr-1 lg:pr-2 py-1.5"
    @click="usePopoverToggle(popover, $event)"
  >
    <div class="flex flex-row items-center gap-x-3">
      <section class="hidden lg:flex flex-col items-end gap-0">
        <p class="text-white font-sora font-medium text-[12px] leading-tight mx-auto">
          {{ user?.name ?? "-" }}
        </p>
        <p class="text-white/70 font-normal text-[10px] leading-tight">
          {{ user?.email ?? "-" }}
        </p>
      </section>

      <div
        class="min-w-[2.4rem] min-h-[2.4rem] lg:min-w-[2.2rem] lg:min-h-[2.2rem] flex justify-center items-center rounded-md bg-primary-300 text-white text-sm font-bold"
      >
        {{ useInitials(user?.name) }}
      </div>
    </div>
  </section>

  <HealthPopover ref="popover" class="dark:bg-dark-800 bg-white max-w-48 w-full" padding="!p-0">
    <ul>
      <li class="md:hidden flex flex-col">
        <ChangeAppearance
          color="!text-gray-700 dark:!text-gray-200"
          class-change-appearance="w-full hover:bg-gray-100 dark:hover:bg-gray-800"
          size="px-3 py-2"
          justify="justify-start"
          label
          label-class="block text-xs font-semibold"
          icon-class="min-w-[1rem] min-h-[1rem]"
        />

        <Notifications
          class="w-full hover:bg-gray-100 dark:hover:bg-gray-800"
          size="px-3 py-2"
          justify="justify-start"
          label
          label-class="block text-xs font-semibold text-gray-700 dark:text-gray-200"
          icon-class="min-w-[1rem] min-h-[1rem]"
        />
      </li>

      <li>
        <div v-for="(option, idx) in options" :key="idx" class="flex flex-col">
          <RouterLink
            :to="option.to"
            class="flex items-center gap-2 text-xs font-semibold hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-2"
            @click.prevent="usePopoverHide(popover, $event)"
          >
            <Icon icon="lucide:user" class="min-w-[1rem] min-h-[1rem]" />
            <p>{{ t(option.name) }}</p>
          </RouterLink>
          <div
            class="flex items-center gap-2 text-xs font-semibold text-red-500 dark:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-2 cursor-pointer"
            @click="((modals.logout = true), usePopoverHide(popover, $event))"
          >
            <Icon icon="solar:logout-2-outline" class="min-w-[1rem] min-h-[1rem]" />
            <p>{{ t("logout") }}</p>
          </div>
        </div>
      </li>
    </ul>
  </HealthPopover>
</template>

<script setup lang="ts">
  import { useI18n } from "vue-i18n";
  import { ref } from "vue";
  import { usePopoverHide, usePopoverToggle } from "@/shared/composables/usePopover";
  import { Icon } from "@iconify/vue";
  import { useAuth } from "@/shared/stores/auth";
  import { storeToRefs } from "pinia";
  import { useInitials } from "@/shared/composables/useInitials";
  import HealthPopover from "@/shared/components/utils/HealthPopover.vue";
  import HealthLogout from "@/shared/components/utils/HealthLogout.vue";
  import ChangeAppearance from "@/app/header/change_appearance/ChangeAppearance.vue";
  import Notifications from "@/app/header/notifications/components/Notifications.vue";

  const popover = ref();

  const { t } = useI18n();

  const modals = ref({ logout: false });

  const authStore = useAuth();

  const { user } = storeToRefs(authStore);

  const options = ref([
    {
      name: "view_profile",
      to: `/user/${user.value.id}`,
    },
  ]);

  defineProps({
    display: {
      type: String,
      default: "flex",
    },
    align: {
      type: String,
      default: "items-center",
    },
    gap: {
      type: String,
      default: "gap-1.5",
    },
  });
</script>
